import { Controller } from "@hotwired/stimulus"
import mapboxgl from 'mapbox-gl'
import { LayerManager } from '../../lib/mapbox/layer_manager'
import { Legend } from '../../lib/mapbox/legend'

export default class extends Controller {
  static values = {
    geojsonFiles: Array,
    token: String,
    mapStyle: String,
    center: Array,
    zoom: Object,
    bounds: Array,
    calculatedBounds: Array
  }

  // Lifecycle methods
  connect() {
    this.initializeMap()
    this.setupManagers()
    this.setupControls()
    this.loadLayers()
  }

  disconnect() {
    this.cleanup()
  }

  // Map initialization
  initializeMap() {
    mapboxgl.accessToken = this.tokenValue

    this.map = new mapboxgl.Map({
      container: this.element,
      style: this.mapStyleValue,
      center: this.centerValue,
      zoom: this.zoomValue.initial,
      minZoom: this.zoomValue.min,
      maxZoom: this.zoomValue.max,
      maxBounds: this.boundsValue
    })
  }

  // Manager setup
  setupManagers() {
    this.layerManager = new LayerManager(this.map)
    this.legend = new Legend(this.map, this.layerManager)
  }

  setupControls() {
    this.legend.addNavigationControls()
    this.legend.addFullscreenControl()
  }

  // Layer loading
  async loadLayers() {
    this.map.on('load', async () => {
      const sortedFiles = this.getSortedGeoJSONFiles()

      this.legend.createLayerToggleControl()

      await this.loadAllLayers(sortedFiles)

      this.fitMapToBounds()
    })
  }

  async loadAllLayers(files) {
    await Promise.all(
      files.map((item, index) => this.loadSingleLayer(item, index))
    )
  }

  async loadSingleLayer(item, index) {
    try {
      const { layer } = await this.loadLayerItem(item, index)
      this.legend.addLayerToControlSorted(layer)
    } catch (error) {
      console.error('Error loading layer:', error)
    }
  }

  async loadLayerItem(item, index) {
    if (typeof item === 'string') {
      return await this.layerManager.loadGeoJSON(item, index)
    }

    const name = item?.name || `Layer ${index + 1}`
    const data = item?.data
    return await this.layerManager.loadGeoJSONData(data, name, index)
  }

  fitMapToBounds() {
    if (this.hasCalculatedBoundsValue && this.calculatedBoundsValue) {
      this.map.fitBounds(this.calculatedBoundsValue, { padding: 50 })
    }
  }

  // Helper methods
  getSortedGeoJSONFiles() {
    return [...this.geojsonFilesValue].sort((a, b) =>
      this.extractLayerName(a).localeCompare(this.extractLayerName(b))
    )
  }

  extractLayerName(item) {
    if (typeof item === 'string') return item.split('/').pop().toLowerCase()
    return (item?.name || '').toString().toLowerCase()
  }

  cleanup() {
    if (this.map) {
      this.map.remove()
      this.map = null
    }

    this.layerManager = null
    this.legend = null
  }
}
